// The local microphone (TS-3): ONE getUserMedia stream serves both the mic ring (UI-C19, getUserMedia + an
// AnalyserNode) and publishing (MAJLIS-9, A-3: media/publish.ts publishes `track`). iOS Safari must not open the
// microphone twice, so nothing else calls getUserMedia. MAJLIS-71 (D-52): it is opened once per room and AGAIN only
// when its track has ENDED (media/micKeeper.ts), always through this one call, the ended track stopped first.
// Echo cancellation and noise suppression stay ON; MAJLIS-73 turns automatic GAIN off and asks for voice isolation, and
// each constant below says why and from what source.
// MAJLIS-116 (T-147, DIAGNOSTIC ONLY, no product default changes): `microphoneOpener` takes an `echoCancellation`
// choice (default true, today's product behaviour). This is the ONE getUserMedia opener still (TS-3) - the choice is
// PASSED IN, not a second opener. Only micdiag.tsx's `?micdiag=1&ec=0` ever passes `false`, to test the owner's report
// that iOS voice processing (tied to echoCancellation) ramps gain after a pause.
import type { MicPermission } from "./micRing";
import { keepRingRunning, type ResumeReport } from "./ringResume";

/** MAJLIS-73 (a), the owner's report "the audio mic is too sensitive it interpret voices from far and try to
 * process". SOURCE: the consultant's ruling (a), 2026-09-22, on the W3C mediacapture-streams `autoGainControl`
 * constraint. AGC raises the capture gain toward a normal level; the INTENTION of turning it off is that a voice
 * across the room is not amplified toward the seat's own. Echo cancellation and noise suppression stay ON: they
 * are not gain. Its effect is unmeasured on a real phone until the owner's next session (AGC lives in the phone's
 * capture path; no file-fed benchmark can show what turning it off does). On our files, these settings do not
 * remove far voices; the speaker gate (engine real/speaker_gate.py) is what can.
 * Pinned by mic.test.ts at the getUserMedia boundary. */
const AUTO_GAIN_CONTROL = false;

/** MAJLIS-73 (d), the consultant's ruling (d), 2026-09-22. SOURCE: the W3C Media Capture Extensions
 * `voiceIsolation` constraint (w3c.github.io/mediacapture-extensions/#voiceisolation-constraint) - ask the
 * platform to keep the voice closest to the microphone and suppress the rest of the room.
 *
 * AN IDEAL, NEVER A REQUIRED, CONSTRAINT, and that is what `{ ideal: true }` says: an `ideal` value is
 * best-effort, and a constraint name the user agent does not know is ignored, so NEITHER CAN RAISE
 * OverconstrainedError and no platform loses its microphone over this. Only `exact` (or min/max) would make it
 * required; mic.test.ts fails if it ever becomes so.
 *
 * APPLIED ONLY WHERE THE PLATFORM SUPPORTS IT, IGNORED ELSEWHERE. What was checked, 2026-09-22 (not memory):
 * - Chromium: chromestatus feature 5106413661847552, "Enabled by default" from Chrome 121 on desktop, no Android
 *   milestone; its own summary says it "only takes effect on platforms where there is low-level support for
 *   voice-isolation style denoising. Currently this is limited to a selected number of ChromeOS devices".
 * - Firefox: "No signal" on chromestatus; mozilla/standards-positions#982 open with no position.
 * - Safari/WebKit: "No signal" on chromestatus; WebKit/standards-positions#314 open with no position.
 * - MDN browser-compat-data 8.1.2 has no entry for it at all.
 * So on the owner's iPhone this line is, today, a no-op (iOS "Mic Mode -> Voice Isolation" in Control Center is a
 * user-side switch, not this constraint).
 * Its effect is unmeasured on a real phone until the owner's next session. */
const VOICE_ISOLATION: ConstrainBoolean = { ideal: true };

/** The one audio constraint set. `voiceIsolation` is added by hand because TypeScript 5.7's lib.dom.d.ts (the
 * version web/ pins) has no such member (checked 2026-09-22). */
type AudioConstraints = MediaTrackConstraints & { voiceIsolation?: ConstrainBoolean };

/** MAJLIS-116 (T-147, DIAGNOSTIC ONLY): the two constraint sets `open` can request, chosen by `echoCancellation`.
 * `ec=0` (micdiag.tsx) also turns noiseSuppression and autoGainControl off - the consultant's brief: isolate iOS's
 * echoCancellation-tied voice processing, so the OTHER constraints must not be a second variable. `voiceIsolation`
 * is left UNCHANGED across both sets, on purpose: it is a no-op on Safari/WebKit today (see VOICE_ISOLATION above,
 * "No signal"), so it cannot be the ramp's cause, and changing it too would make the on/off comparison test two
 * things at once instead of one. */
function audioConstraints(echoCancellation: boolean): AudioConstraints {
  return {
    echoCancellation,
    noiseSuppression: echoCancellation ? true : false,
    autoGainControl: echoCancellation ? AUTO_GAIN_CONTROL : false,
    voiceIsolation: VOICE_ISOLATION,
  };
}

export interface MicSource {
  /** The one audio track this stream opened; the publisher sends this same track (A-3, TS-3). */
  readonly track: MediaStreamTrack | null;
  /** RMS of the latest time-domain frame, 0..1. */
  level(): number;
  setEnabled(on: boolean): void;
  stop(): void;
}

/** What an open gives back. `error` is the refusal's NAME only (MAJLIS-71, D-52: the keeper tells an explicit permission
 * denial from any other failure of a re-open tap); absent on success. */
export interface MicOpened {
  permission: MicPermission;
  source: MicSource | null;
  error?: string;
}

export type MicOpener = () => Promise<MicOpened>;

/** MAJLIS-71 (the `?micdiag=1` panel, micdiag.tsx): told of each getUserMedia call and its result, as a track COUNT or
 * the error's NAME. It is handed nothing else - no stream, no track, no device, no message - so it can record nothing
 * else. Only the flag-gated opener carries one; `openMicrophone` has none.
 * MAJLIS-98: `resume`, if given, is told of each resume() the ring's keeper tried (ringResume.ts): the trigger and the
 * error's NAME, nothing else. */
export interface GumObserver {
  call(): void;
  result(r: { tracks: number } | { error: string }): void;
  resume?: ResumeReport;
}

let sharedContext: AudioContext | null = null;

/** MAJLIS-71: the mic ring's AudioContext (null until primeAudio made one), for the diagnostics panel to READ its
 * state. Nothing outside this module creates or closes it; only this module and the keeper it attaches to an open
 * microphone (MAJLIS-98, ringResume.ts) resume it. */
export function micAudioContext(): AudioContext | null {
  return sharedContext;
}

/** Create/resume the AudioContext inside the user's tap (iOS needs the gesture). */
export function primeAudio(): void {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    sharedContext ??= new Ctx();
    void sharedContext.resume();
  } catch {
    // no audio in this browser: the ring shows "no microphone" when the mic opens
  }
}

/** The one getUserMedia opener (TS-3). `observe` is MAJLIS-71's diagnostics hook; it changes nothing it observes.
 * `echoCancellation` (MAJLIS-116, T-147, DIAGNOSTIC ONLY) defaults to true - today's product constraints, unchanged;
 * only micdiag.tsx's `?micdiag=1&ec=0` ever passes false. */
export function microphoneOpener(observe?: GumObserver, echoCancellation = true): MicOpener {
  return () => open(observe, echoCancellation);
}

export const openMicrophone: MicOpener = microphoneOpener();

async function open(observe: GumObserver | undefined, echoCancellation: boolean): Promise<MicOpened> {
  if (!navigator.mediaDevices?.getUserMedia) {
    observe?.result({ error: "Unsupported" });
    return { permission: "unavailable", source: null, error: "Unsupported" };
  }
  let stream: MediaStream;
  try {
    const audio: AudioConstraints = audioConstraints(echoCancellation);
    observe?.call();
    stream = await navigator.mediaDevices.getUserMedia({ audio });
  } catch (e) {
    const name = (e as { name?: string }).name;
    const error = typeof name === "string" ? name : "Error";
    observe?.result({ error });
    return { permission: name === "NotFoundError" || name === "OverconstrainedError" ? "unavailable" : "denied", source: null, error };
  }
  observe?.result({ tracks: stream.getAudioTracks().length });
  primeAudio();
  const ctx = sharedContext;
  if (!ctx) {
    stream.getTracks().forEach((t) => t.stop());
    return { permission: "unavailable", source: null };
  }
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  const node = ctx.createMediaStreamSource(stream);
  node.connect(analyser);
  const buf = new Float32Array(analyser.fftSize);
  const tracks = stream.getAudioTracks();
  // MAJLIS-98: after an iOS interruption the context can stay suspended while the track is live; keep it running for as
  // long as this microphone is open (the same context and track - never a second of either, TS-3)
  const release = keepRingRunning(ctx, tracks[0] ?? null, document, window, observe?.resume);
  return {
    permission: "granted",
    source: {
      track: tracks[0] ?? null,
      level() {
        if (!tracks.some((t) => t.enabled)) return 0;
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) sum += v * v;
        return Math.min(1, Math.sqrt(sum / buf.length));
      },
      setEnabled(on) {
        tracks.forEach((t) => (t.enabled = on));
      },
      stop() {
        release();
        node.disconnect();
        stream.getTracks().forEach((t) => t.stop());
      },
    },
  };
}
